import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const values = [
  {
    number: "01",
    title: "Mission-Driven",
    description:
      "Bridging the gap between quality products and businesses worldwide through reliable trading and e-commerce solutions.",
  },
  {
    number: "02",
    title: "Customer First",
    description:
      "Every partnership is built on trust, transparency, and a deep commitment to understanding your unique needs.",
  },
  {
    number: "03",
    title: "Quality Assured",
    description:
      "Rigorous quality checks ensure every product meets international standards before reaching you.",
  },
  {
    number: "04",
    title: "Growth Partner",
    description:
      "We don't just supply products—we help you scale your business with strategic trading and e-commerce support.",
  },
];

export function AboutSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="about" className="py-32 bg-card relative">
      {/* Subtle line accents */}
      <div className="absolute top-0 left-0 w-full h-px bg-border" />
      
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left: Content */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-xs tracking-wide-elegant uppercase text-muted-foreground mb-6">
              About Us
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-foreground mb-8 leading-[1.1] font-medium">
              India's Leading
              <br />
              <span className="text-muted-foreground">Trading Company</span>
            </h2>
            <div className="space-y-6">
              <p className="text-muted-foreground text-lg leading-relaxed">
                <span className="font-logo font-semibold text-foreground">SAILKHA TRADE</span> is a
                premier trading and e-commerce company based in India, sourcing and distributing
                industrial, construction, and infrastructure products for businesses across the
                country and around the world.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Our purpose is straightforward — connect businesses with quality products at
                competitive wholesale prices, supported by reliable trading expertise and seamless
                e-commerce solutions.
              </p>
            </div>
            
            {/* Quote */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mt-12 pl-6 border-l-2 border-foreground"
            >
              <p className="text-xl text-foreground font-display italic">
                "Where quality meets opportunity, success follows."
              </p>
              <p className="text-sm text-muted-foreground mt-3 tracking-wide-elegant uppercase">
                — The Sailkha Philosophy
              </p>
            </motion.div>
          </motion.div>

          {/* Right: Values */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            {values.map((value, i) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, x: 20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
                className="group p-6 border border-border hover:border-foreground/30 transition-all duration-300"
              >
                <div className="flex items-start gap-6">
                  <span className="text-3xl font-display text-muted-foreground/40 group-hover:text-foreground transition-colors">
                    {value.number}
                  </span>
                  <div>
                    <h3 className="font-display text-xl text-foreground mb-2">
                      {value.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
